import { useState, useEffect } from "react";
import {
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter,
    Button,
    Typography,
    Input,
    IconButton,
    Tooltip,
} from "@material-tailwind/react";
import MaterialIcon from "components/material-icon";
import AddNewThemeDialog from "./add-new-theme";
import EditThemeDialog from "./edit-theme";
import { toast } from "react-toastify";
import { fetcherFull } from "utils/api";
import { createSurveyCacheInvalidator } from "utils/invalidate-survey-caches";
import { deleteTheme } from "../utils/delete-theme";
import { updateThemeName } from "../utils/update-theme-name";
import {
    GobbyTable,
    GobbyTableHead,
    GobbyTableBody,
    GobbyTableRow,
    GobbyTableHeader,
    GobbyTableCell,
} from "components/ui/gobby-table";

export default function ManageThemesDialog({
    open,
    onClose,
    themes,
    // Full-universe counts regardless of active filters (#983).
    themeCounts,
    surveyId,
    themesDataMutate,
    customResponsesFilterMutate,
}) {
    const [searchTerm, setSearchTerm] = useState("");

    const invalidateSurveyCaches = createSurveyCacheInvalidator(surveyId);

    const [showEditDialog, setShowEditDialog] = useState(false);
    const [selectedTheme, setSelectedTheme] = useState(null);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [themeToDelete, setThemeToDelete] = useState(null);
    const [showAddNewThemeDialog, setShowAddNewThemeDialog] = useState(false);

    // Reset search term when dialog opens
    useEffect(() => {
        if (open) setSearchTerm("");
    }, [open]);

    // Filter themes based on search term
    const filteredThemes = themes?.filter((theme) =>
        theme.name.toLowerCase().includes(searchTerm.toLowerCase())
    ) || [];

    const handleOpenEdit = (theme) => {
        setSelectedTheme(theme);
        setShowEditDialog(true);
    };

    const handleOpenDelete = (theme) => {
        setThemeToDelete(theme);
        setShowDeleteDialog(true);
    };

    const handleDeleteTheme = async (id) => {
        const ok = await deleteTheme({
            surveyId,
            themeId: id,
            themesDataMutate,
            customResponsesFilterMutate,
            invalidateSurveyCaches,
        });
        if (ok) {
            setShowDeleteDialog(false);
        }
    };

    const handleEditThemeName = async (id, newName) => {
        const { ok } = await updateThemeName({ surveyId, themeId: id, name: newName });
        if (ok) {
            themesDataMutate();
            customResponsesFilterMutate();
            invalidateSurveyCaches();
            toast.success("Theme Edited");
            setShowEditDialog(false);
        }
    };

    const handleAddNewTheme = async (name) => {
        if (!name?.trim()) return;

        let response;
        try {
            response = await fetcherFull(
                `/v2/survey/${surveyId}/theme`,
                "POST",
                { name: name.trim() },
            );
        } catch (err) {
            toast.error(`Error creating Theme, ${err?.message || "Unknown error"}`);
            console.error(err);
            return;
        }

        if (response && response?.id) {
            themesDataMutate();
            customResponsesFilterMutate();
            invalidateSurveyCaches();
            toast.success("Theme Created");
            setShowAddNewThemeDialog(false);
        } else {
            const serverError = response || "Unknown issue creating Theme.";
            toast.error(`Error creating Theme, ${serverError}`);
            console.log(response?.errors);
        }
    };

    const handleClose = () => {
        setSearchTerm("");
        setShowEditDialog(false);
        setShowDeleteDialog(false);
        setShowAddNewThemeDialog(false);
        setSelectedTheme(null);
        setThemeToDelete(null);
        onClose();
    };

    return (
        <>
            <Dialog open={open && !showEditDialog && !showDeleteDialog && !showAddNewThemeDialog} handler={handleClose} className="dialog" size="lg">
                <DialogHeader className="dialog__header">
                    <Typography variant="h4">Manage Themes</Typography>
                </DialogHeader>

                <DialogBody className="dialog__body">
                    <div className="dialog__section flex flex-col gap-4">
                        {/* Search and Create */}
                        <div className="flex gap-2 items-center">
                            <div className="relative flex-1">
                                <Input
                                    type="text"
                                    label="Search Themes"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder="Search by theme name"
                                    className="font-body"
                                />
                                {searchTerm && (
                                    <MaterialIcon
                                        name="close"
                                        className="absolute right-2 top-1/2 transform -translate-y-1/2 cursor-pointer text-gray-500"
                                        onClick={() => setSearchTerm("")}
                                    />
                                )}
                            </div>
                            <Button
                                className="btn btn--primary"
                                onClick={() => setShowAddNewThemeDialog(true)}
                            >
                                <MaterialIcon name="add" className="mr-1" />
                                Create New Theme
                            </Button>
                        </div>

                        {/* Themes Table */}
                        <div className="overflow-auto max-h-96">
                            <GobbyTable>
                                <GobbyTableHead>
                                    <GobbyTableRow>
                                        <GobbyTableHeader>Theme Name</GobbyTableHeader>
                                        <GobbyTableHeader>Response Count</GobbyTableHeader>
                                        <GobbyTableHeader>Actions</GobbyTableHeader>
                                    </GobbyTableRow>
                                </GobbyTableHead>
                                <GobbyTableBody>
                                    {filteredThemes.length === 0 ? (
                                        <GobbyTableRow>
                                            <GobbyTableCell colSpan={3}>
                                                <Typography variant="paragraph" className="text-center">
                                                    {searchTerm
                                                        ? "No themes match your search"
                                                        : "No themes created yet"}
                                                </Typography>
                                            </GobbyTableCell>
                                        </GobbyTableRow>
                                    ) : (
                                        filteredThemes.map((theme) => (
                                            <GobbyTableRow key={theme.id}>
                                                <GobbyTableCell>
                                                    <Typography variant="paragraph">
                                                        {theme.name}
                                                    </Typography>
                                                </GobbyTableCell>
                                                <GobbyTableCell>
                                                    <Typography variant="paragraph">
                                                        {themeCounts?.[theme.id] || 0}
                                                    </Typography>
                                                </GobbyTableCell>
                                                <GobbyTableCell>
                                                    <div className="flex gap-2">
                                                        <Tooltip content="Edit Theme">
                                                            <IconButton
                                                                variant="text"
                                                                className="p-1"
                                                                onClick={() => handleOpenEdit(theme)}
                                                            >
                                                                <MaterialIcon name="edit" color="black" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip content="Delete Theme">
                                                            <IconButton
                                                                variant="text"
                                                                className="p-1"
                                                                onClick={() => handleOpenDelete(theme)}
                                                            >
                                                                <MaterialIcon name="delete" color="red" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </div>
                                                </GobbyTableCell>
                                            </GobbyTableRow>
                                        ))
                                    )}
                                </GobbyTableBody>
                            </GobbyTable>
                        </div>
                    </div>
                </DialogBody>

                <DialogFooter className="dialog__footer">
                    <Button
                        type="button"
                        className="btn btn--secondary"
                        onClick={handleClose}
                    >
                        Close
                    </Button>
                </DialogFooter>
            </Dialog>

            {/* Edit Theme Dialog */}
            {showEditDialog && selectedTheme && (
                <EditThemeDialog
                    open={showEditDialog}
                    onClose={() => setShowEditDialog(false)}
                    theme={selectedTheme}
                    onSaveThemeName={handleEditThemeName}
                />
            )}

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={showDeleteDialog}
                handler={() => setShowDeleteDialog(false)}
                size="sm"
                className="dialog"
            >
                <DialogHeader className="dialog__header">
                    <Typography variant="h5" color="red">
                        Confirm Delete
                    </Typography>
                </DialogHeader>

                <DialogBody className="dialog__body">
                    <Typography variant="paragraph" color="gray">
                        This will permanently delete {themeToDelete?.name || "this theme"}?
                    </Typography>
                </DialogBody>

                <DialogFooter className="dialog__footer">
                    <Button
                        className="btn btn--secondary"
                        onClick={() => setShowDeleteDialog(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        className="btn btn--danger"
                        onClick={() => handleDeleteTheme(themeToDelete?.id)}
                    >
                        Delete
                    </Button>
                </DialogFooter>
            </Dialog>

            {/* Add New Theme Dialog */}
            <AddNewThemeDialog
                open={showAddNewThemeDialog}
                onClose={() => setShowAddNewThemeDialog(false)}
                onAddNewTheme={handleAddNewTheme}
            />
        </>
    );
}
